/**
 * Base Customer Types
 * Common fields shared by all customer types
 */

import type { GuestPushRules } from '../../pages/guestPushRules';

export type CustomerType = 'condominio' | 'empresa' | 'fabrica';

export interface BaseCustomer {
  id: string;
  name: string;
  type: CustomerType;
  address: string;
  city: string;
  state: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  description?: string;
  logo?: string;
  photo_url?: string;
  internal_code?: string;
  portaria_type?: 'presencial' | 'autonoma' | 'virtual' | 'none';
  phase?: 'production' | 'implementation' | 'disabled';
  status?: 'active' | 'inactive' | 'suspended';
  porteiro_reports_enabled?: boolean;
  porteiro_can_register_persons?: boolean;
  porteiro_can_manage_keys?: boolean;
  porteiro_can_manage_contractors?: boolean;
  porteiro_botoeira_enabled?: boolean;
  porteiro_can_edit_cpf?: boolean;
  porteiro_can_approve_pre_registrations?: boolean;
  porteiro_can_approve_self_registrations?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CustomerFilterState {
  selectedCustomers: string[];
  selectedTypes: CustomerType[];
  searchTerm: string;
}

export interface CustomerApiResponse<T = BaseCustomer> {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
}

// Re-export from specific files
export type { Condominio } from './condominio';
export type { Empresa } from './empresa';
export type { Fabrica } from './fabrica';

// Visitor Entrance Types
export interface VisitorEntranceType {
  id: string;
  label: string;
  icon: string;
  action: 'auto' | 'manual';
}

// Customer Settings Types
export interface VisitorSettings {
  visitor_entry_window_minutes: number;
  visitor_exit_window_hours: number;
  visitor_passage_credits: number;
  face_removal_delay_enabled: boolean;
  face_removal_delay_minutes: number;
  whatsapp_notification_template?: string;
  visitor_custom_period_enabled?: boolean;
  visitor_custom_period_max_days?: number;
}

export const DEFAULT_WHATSAPP_TEMPLATE = `Olá {{nome_responsavel}}!

Um {{tipo}} deseja entrar no {{condominio}}.

Nome: {{nome_visitante}}
Data/Hora: {{data_hora}}
Destino: {{destino}}

Você autoriza a entrada?
Responda SIM para permitir ou NÃO para negar.`;

export interface CustomerBasicInfo {
  name: string;
  postal_code?: string; // CNPJ
  address: string;
  city: string;
  state: string;
  country?: string;
  phone?: string;
  email?: string;
  description?: string;
  internal_code?: string;
  portaria_type?: 'presencial' | 'autonoma' | 'virtual' | 'none';
  phase?: 'production' | 'implementation' | 'disabled';
}

export interface PersonFieldDefinition {
  enabled: boolean;
  required: boolean;
  label?: string;
  order?: number;      // sort position across all configurable fields (standard + custom)
  col_span?: 1 | 2;   // 1 = half width, 2 = full width. Default: 1
}

export interface CustomPersonField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'phone' | 'date' | 'select' | 'textarea';
  required: boolean;
  options?: string[];
  order?: number;      // shared namespace with standard fields
  col_span?: 1 | 2;   // Default: 2 (preserves current full-width behavior)
}

/**
 * An admin-created extra field on a DEPARTAMENTO — CNPJ, nome fantasia, centro de custo.
 *
 * An empresa's departments are its tenants, contractors and sister companies, so the data that
 * matters is the company's, and which fields exist differs per empresa. Declared under
 * Configurações → Campos, exactly as person fields are.
 *
 * Mirrors Go's models.CustomDepartmentField. Deliberately separate from CustomPersonField
 * despite the near-identical shape: the two evolve for different reasons.
 */
export interface CustomDepartmentField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'phone' | 'date' | 'select' | 'textarea';
  required: boolean;
  options?: string[];
  order?: number;
}

export interface DepartmentFieldConfig {
  custom_fields?: CustomDepartmentField[];
}

/** Values keyed by field id, as stored on the departamento. Strings — see the Go model. */
export type DepartmentCustomFieldValues = Record<string, string>;

export interface PhotoRequirementsConfig {
  identity_photo?: boolean;
  document_front?: boolean;
  document_back?: boolean;
}

export interface PersonRegistrationFieldConfig {
  standard_fields?: Record<string, PersonFieldDefinition>; // "cpf" | "email" | "phone" | "date_of_birth" | "rg"
  custom_fields?: CustomPersonField[];
  photo_requirements?: PhotoRequirementsConfig;
}

export interface CondoDataSettings {
  /** Visita a múltiplos destinos — one visitor, several unidades/departamentos. Off by default. */
  multi_destination_visits_enabled?: boolean;
  pets_enabled?: boolean;
  time_zones_enabled?: boolean;
  visitor_schedule_inherit_enabled?: boolean;
  guarita_integration_enabled?: boolean;
  common_areas_enabled?: boolean;
  common_areas_max_concurrent_bookings_per_unit?: number;
  portao_online_enabled?: boolean;
  portao_online_device_ids?: string[];
  portao_online_roles?: string[];
  camera_online_enabled?: boolean;
  camera_online_ids?: string[];
  camera_online_roles?: string[];
  obras_enabled?: boolean;
  aso_enabled?: boolean;
  aso_applies_to?: string[]; // ["moradores","funcionarios","prestadores","visitantes"]
  aso_block_access?: boolean;
  /**
   * Who may liberar visitantes. OFF BY DEFAULT — absent reads as "anybody", which is how every
   * existing customer behaves today. An empty relation list also means everybody; see
   * pages/visitorAuthorizer.ts and the Go policy it mirrors.
   */
  visitor_authorization_control_enabled?: boolean;
  visitor_authorizer_relations?: string[];
  person_type_field_configs?: Record<string, PersonRegistrationFieldConfig>;
  sindico_dashboard_enabled?: boolean;
  resident_invite_by_owner_enabled?: boolean;
  time_spent_enabled?: boolean;
  keys_enabled?: boolean;
  livro_enabled?: boolean;
  /** Achados e Perdidos — the portaria publishes what it is holding. Off by default. */
  lost_found_enabled?: boolean;
  /** Troca de plantão (registro de passagem de posto). Off unless a customer asks for it. */
  troca_plantao_enabled?: boolean;
  /** Termo de liberação para menores. Off by default: turning it on makes date_of_birth
   *  required when adding a dependente, which older app versions do not send. */
  termo_menores_enabled?: boolean;
  /** Solicitações (credential/access requests from residents). Backfilled true for existing
   *  customers — see migration 229 — because the feature predates the flag. */
  solicitacoes_enabled?: boolean;
  /** Residents see visitante/prestador access events. ABSENT MEANS TRUE — see
   *  services.GuestEventPolicyForCustomer; never read this with `!!`. */
  guest_events_enabled?: boolean;
  /** …and are notified when one arrives or leaves. Absent means true; forced off when
   *  `guest_events_enabled` is false. */
  guest_event_push_enabled?: boolean;
  /** Which of those notifications are wanted: {visitante, prestador} × {entrada, saída}. Each key
   *  absent means true, per rule — see pages/guestPushRules.ts and models.GuestEventPushRules. */
  guest_event_push_rules?: GuestPushRules;
  alphadigi_lpr_enabled?: boolean;
  hikvision_lpr_enabled?: boolean;
  normas_internas_enabled?: boolean;
  ocorrencias_enabled?: boolean;
  ocorrencias_resident_visibility?: string; // "all" | "admins_only"
  mural_anuncios_enabled?: boolean;
  mural_anuncios_comments_mode?: 'disabled' | 'optional';
  force_uppercase_enabled?: boolean;
  mobile_app_enabled?: boolean;
  encomendas_enabled?: boolean;
  visitantes_enabled?: boolean;
  self_registration_cpf_required?: boolean;
  self_registration_identity_photo_required?: boolean;
  self_registration_doc_photo_required?: boolean;
  self_registration_phone_required?: boolean;
  funcionarios_enabled?: boolean;
  /** What a dependente/familiar/agregado may do. Absent map — or an absent key inside it —
   *  means ALLOWED: these capabilities were ungated before the feature existed, so nothing may
   *  silently take them away. See DependentPermissionsEditor and condo-manager's
   *  services/dependent_permission_policy.go. */
  dependent_permissions?: Record<string, { enabled: boolean; min_age?: number }>;
}

export interface EmpresaDataSettings {
  /** Visita a múltiplos destinos — one visitor, several unidades/departamentos. Off by default. */
  multi_destination_visits_enabled?: boolean;
  /** Extra fields every departamento of this empresa carries. Empresa-only. */
  department_field_config?: DepartmentFieldConfig;
  time_zones_enabled?: boolean;
  visitor_schedule_inherit_enabled?: boolean;
  guarita_integration_enabled?: boolean;
  alphadigi_lpr_enabled?: boolean;
  hikvision_lpr_enabled?: boolean;
  obras_enabled?: boolean;
  aso_enabled?: boolean;
  aso_applies_to?: string[];
  aso_block_access?: boolean;
  /**
   * Who may liberar visitantes. OFF BY DEFAULT — absent reads as "anybody", which is how every
   * existing customer behaves today. An empty relation list also means everybody; see
   * pages/visitorAuthorizer.ts and the Go policy it mirrors.
   */
  visitor_authorization_control_enabled?: boolean;
  visitor_authorizer_relations?: string[];
  portao_online_enabled?: boolean;
  portao_online_device_ids?: string[];
  portao_online_roles?: string[];
  camera_online_enabled?: boolean;
  camera_online_ids?: string[];
  camera_online_roles?: string[];
  time_spent_enabled?: boolean;
  keys_enabled?: boolean;
  livro_enabled?: boolean;
  /** Achados e Perdidos — the portaria publishes what it is holding. Off by default. */
  lost_found_enabled?: boolean;
  /** Troca de plantão (registro de passagem de posto). Off unless a customer asks for it. */
  troca_plantao_enabled?: boolean;
  /** Termo de liberação para menores. Off by default: turning it on makes date_of_birth
   *  required when adding a dependente, which older app versions do not send. */
  termo_menores_enabled?: boolean;
  /** Solicitações (credential/access requests from residents). Backfilled true for existing
   *  customers — see migration 229 — because the feature predates the flag. */
  solicitacoes_enabled?: boolean;
  /** Residents see visitante/prestador access events. ABSENT MEANS TRUE — see
   *  services.GuestEventPolicyForCustomer; never read this with `!!`. */
  guest_events_enabled?: boolean;
  /** …and are notified when one arrives or leaves. Absent means true; forced off when
   *  `guest_events_enabled` is false. */
  guest_event_push_enabled?: boolean;
  /** Which of those notifications are wanted: {visitante, prestador} × {entrada, saída}. Each key
   *  absent means true, per rule — see pages/guestPushRules.ts and models.GuestEventPushRules. */
  guest_event_push_rules?: GuestPushRules;
  mural_anuncios_enabled?: boolean;
  mural_anuncios_comments_mode?: 'disabled' | 'optional';
  force_uppercase_enabled?: boolean;
  mobile_app_enabled?: boolean;
  encomendas_enabled?: boolean;
  visitantes_enabled?: boolean;
  self_registration_cpf_required?: boolean;
  self_registration_identity_photo_required?: boolean;
  self_registration_doc_photo_required?: boolean;
  self_registration_phone_required?: boolean;
}

export interface SmtpConfig {
  enabled: boolean;
  host: string;
  port: number;
  ssl: boolean;
  user: string;
  password_set?: boolean; // read: true when password is stored (never returned in plaintext)
  password?: string;      // write: new password (leave blank to keep existing)
  from_name: string;
  from_email: string;
  updated_at?: string;
}

export interface CustomerSettings {
  customer_id: string;
  customer_name: string;
  customer_type: CustomerType;
  photo_url?: string;
  basic_info: CustomerBasicInfo;
  visitor_settings: VisitorSettings;
  condo_data?: CondoDataSettings;
  empresa_data?: EmpresaDataSettings;
  visitor_entrance_types?: VisitorEntranceType[];
  porteiro_reports_enabled?: boolean;
  porteiro_can_register_persons?: boolean;
  porteiro_can_manage_keys?: boolean;
  porteiro_can_manage_contractors?: boolean;
  porteiro_botoeira_enabled?: boolean;
  porteiro_can_edit_cpf?: boolean;
  porteiro_can_approve_pre_registrations?: boolean;
  porteiro_can_approve_self_registrations?: boolean;
  smtp_config?: SmtpConfig;
}

export interface CustomerSettingsUpdateRequest {
  basic_info?: Partial<CustomerBasicInfo>;
  visitor_settings?: Partial<VisitorSettings>;
}

export const DEFAULT_VISITOR_SETTINGS: VisitorSettings = {
  visitor_entry_window_minutes: 15,
  visitor_exit_window_hours: 14,
  visitor_passage_credits: 0,
  face_removal_delay_enabled: false,
  face_removal_delay_minutes: 5,
  visitor_custom_period_enabled: true,
};
